import { isAxiosError } from "axios";

import { apiClient } from "@/lib/api";

export type GeocodingProviderType = "nominatim" | "google" | "yandex";

/** Single-point geocode result. lat/lon are JSON numbers from the backend. */
export interface GeocodeAddressResult {
  lat: number;
  lon: number;
  provider: string | null;
}

/** Autocomplete suggestion. lat/lon are strings in this endpoint's payload. */
export interface AddressSuggestion {
  lat: string;
  lon: string;
  display_name: string;
}

/**
 * Geocodes a single address string through the backend (BUG-388/391 normalized
 * path), so verbose Belarusian addresses resolve.
 * POST /api/v1/orders/geocode/
 *
 * Returns coordinates on success, or `null` when the backend finds no match
 * (HTTP 422) — 422 is treated as "not found", not an error. Any other error
 * (network, 4xx/5xx) is rethrown for the caller to handle.
 */
export async function geocodeAddress(
  address: string,
): Promise<GeocodeAddressResult | null> {
  try {
    const response = await apiClient.post<{
      lat: number | string;
      lon: number | string;
      provider: string | null;
    }>("/v1/orders/geocode/", { address });
    const data = response.data;
    return {
      lat: Number(data.lat),
      lon: Number(data.lon),
      provider: data.provider,
    };
  } catch (error) {
    // 422 = no match found; surface as null rather than an error.
    if (isAxiosError(error) && error.response?.status === 422) {
      return null;
    }
    throw error;
  }
}

/** Reverse-geocode result: a single display address plus the resolving provider. */
export interface ReverseGeocodeResult {
  address: string;
  provider: string | null;
}

/**
 * Resolves coordinates to a display address through the backend (TASK-4996).
 * POST /api/v1/orders/reverse-geocode/
 *
 * The call goes through the server deliberately: the per-tenant provider chain
 * and its API keys live there and must never reach the browser (BUG-587).
 *
 * Returns the address on success, or `null` when the provider matched no
 * location (HTTP 422) — the same "422 is not-found, not an error" convention
 * `geocodeAddress` already uses above. Any other error (network, 429, 4xx/5xx)
 * is rethrown so the caller can tell "no address here" from "you are going too
 * fast" and from "the lookup broke".
 */
export async function reverseGeocode(
  lat: number,
  lon: number,
): Promise<ReverseGeocodeResult | null> {
  try {
    const response = await apiClient.post<{
      address: string;
      provider: string | null;
    }>("/v1/orders/reverse-geocode/", { lat, lon });
    return {
      address: response.data.address,
      provider: response.data.provider,
    };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 422) {
      return null;
    }
    throw error;
  }
}

/**
 * Fetches address autocomplete suggestions through the backend.
 * GET /api/v1/orders/geocode-suggestions/?q=<query>&limit=<n>
 *
 * Returns the suggestion array on success; on any error returns an empty array
 * (defensive — a failed autocomplete should not break typing).
 */
export async function getAddressSuggestions(
  query: string,
  limit = 5,
): Promise<AddressSuggestion[]> {
  try {
    const response = await apiClient.get<AddressSuggestion[]>(
      "/v1/orders/geocode-suggestions/",
      { params: { q: query, limit } },
    );
    return response.data;
  } catch {
    return [];
  }
}

export interface GeocodingProvider {
  id: number;
  provider_type: GeocodingProviderType;
  /** Never pre-filled on read; empty string means no key stored */
  api_key: string;
  is_active: boolean;
  priority: number;
}

export interface CreateGeocodingProviderPayload {
  provider_type: GeocodingProviderType;
  api_key: string;
  is_active: boolean;
  priority: number;
}

export interface UpdateGeocodingProviderPayload {
  provider_type?: GeocodingProviderType;
  /** Empty string means "do not update the key" */
  api_key?: string;
  is_active?: boolean;
  priority?: number;
}

/**
 * Fetches all geocoding providers for the current tenant.
 * GET /api/v1/orders/geocoding-providers/
 */
export async function listGeocodingProviders(): Promise<GeocodingProvider[]> {
  const response = await apiClient.get<
    GeocodingProvider[] | { results: GeocodingProvider[] }
  >("/v1/orders/geocoding-providers/");
  const data = response.data;
  return Array.isArray(data) ? data : data.results;
}

/**
 * Creates a new geocoding provider.
 * POST /api/v1/orders/geocoding-providers/
 */
export async function createGeocodingProvider(
  data: CreateGeocodingProviderPayload,
): Promise<GeocodingProvider> {
  const response = await apiClient.post<GeocodingProvider>(
    "/v1/orders/geocoding-providers/",
    data,
  );
  return response.data;
}

/**
 * Partially updates an existing geocoding provider.
 * PATCH /api/v1/orders/geocoding-providers/{id}/
 */
export async function updateGeocodingProvider(
  id: number,
  data: UpdateGeocodingProviderPayload,
): Promise<GeocodingProvider> {
  const response = await apiClient.patch<GeocodingProvider>(
    `/v1/orders/geocoding-providers/${id}/`,
    data,
  );
  return response.data;
}

/**
 * Deletes a geocoding provider.
 * DELETE /api/v1/orders/geocoding-providers/{id}/
 */
export async function deleteGeocodingProvider(id: number): Promise<void> {
  await apiClient.delete(`/v1/orders/geocoding-providers/${id}/`);
}
